import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { usePortal } from './PortalContext';
import { mergeScheduleRows } from './portalInquiries';
import PortalNotificationBell from './PortalNotificationBell';

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

/** Same gradient + diagonal bar as `PortalLogin` so the portal shell matches login ↔ dashboard */
const PORTAL_LOGIN_GRADIENT = 'linear-gradient(135deg, #1f2937 0%, #111827 100%)';
const PORTAL_LOGIN_DIAGONAL_BAR = {
  background:
    'linear-gradient(90deg, rgba(220,230,245,0) 0%, rgba(220,230,245,0.28) 8%, rgba(220,230,245,0.78) 18%, rgba(100,115,135,0.45) 30%, rgba(100,115,135,0.45) 70%, rgba(220,230,245,0.78) 82%, rgba(220,230,245,0.28) 92%, rgba(220,230,245,0) 100%)'
};

function mergePortalStyles(base, dark) {
  if (!dark) {
    return {
      ...base,
      // Match the main app day-mode page background.
      container: {
        ...base.container,
        background: '#f1f5f9',
        color: '#0f172a',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      },
      diagonalBackground: {
        ...base.diagonalBackground,
        display: 'none'
      },
      main: {
        ...base.main,
        background: 'transparent'
      }
    };
  }

  /** Dark mode shell matches `PortalLogin` (gradient + diagonal + dark nav). */
  const shell = {
    ...base,
    container: {
      ...base.container,
      background: PORTAL_LOGIN_GRADIENT,
      color: '#e2e8f0',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    diagonalBar: { ...base.diagonalBar, ...PORTAL_LOGIN_DIAGONAL_BAR },
    navbar: {
      ...base.navbar,
      background: 'rgba(15, 23, 42, 0.85)',
      borderBottom: '1px solid #334155'
    },
    navTitle: { ...base.navTitle, color: '#f1f5f9' },
    customerName: { ...base.customerName, color: '#94a3b8' },
    logoutButton: {
      ...base.logoutButton,
      border: '1px solid #475569',
      color: '#cbd5e1'
    },
    welcomeTitle: { ...base.welcomeTitle, color: '#f1f5f9' },
    welcomeSubtitle: { ...base.welcomeSubtitle, color: '#94a3b8' },
    loadingState: {
      ...base.loadingState,
      color: '#94a3b8',
      background: 'rgba(15, 23, 42, 0.25)'
    },
    spinner: {
      ...base.spinner,
      border: '3px solid #334155',
      borderTopColor: '#fb923c'
    },
    emptyState: {
      ...base.emptyState,
      color: '#64748b',
      background: 'rgba(15, 23, 42, 0.25)'
    },
    emptyText: { ...base.emptyText, color: '#94a3b8' },
    errorContainer: {
      ...base.errorContainer,
      background: 'rgba(30, 41, 59, 0.85)',
      border: '1px solid #475569'
    },
    errorTitle: { ...base.errorTitle, color: '#f1f5f9' },
    errorMessage: { ...base.errorMessage, color: '#94a3b8' },
    main: { ...base.main, background: 'transparent' }
  };

  return {
    ...shell,
    errorBox: {
      ...base.errorBox,
      background: 'rgba(127, 29, 29, 0.25)',
      border: '1px solid rgba(248, 113, 113, 0.4)',
      color: '#fecaca'
    },
    statCard: {
      ...base.statCard,
      background: 'rgba(30, 41, 59, 0.65)',
      border: '1px solid rgba(71, 85, 105, 0.5)',
      boxShadow: '0 8px 24px rgba(0,0,0,0.35)'
    },
    statLabel: { ...base.statLabel, color: '#94a3b8' },
    statSubtext: { ...base.statSubtext, color: '#64748b' },
    section: {
      ...base.section,
      background: 'rgba(30, 41, 59, 0.45)',
      boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
    },
    sectionHeader: {
      ...base.sectionHeader,
      borderBottom: '1px solid rgba(51, 65, 85, 0.6)',
      background: 'rgba(15, 23, 42, 0.35)'
    },
    sectionTitle: { ...base.sectionTitle, color: '#f1f5f9' },
    tableHeader: {
      ...base.tableHeader,
      background: 'rgba(15, 23, 42, 0.5)',
      borderBottom: '1px solid rgba(51, 65, 85, 0.6)',
      color: '#94a3b8'
    },
    tableRow: {
      ...base.tableRow,
      borderBottom: '1px solid rgba(51, 65, 85, 0.4)',
      background: 'rgba(30, 41, 59, 0.35)'
    },
    invoiceNumber: { ...base.invoiceNumber, color: '#f1f5f9' },
    dateCol: { ...base.dateCol, color: '#94a3b8' },
    amountCol: { ...base.amountCol, color: '#f1f5f9' },
    jobCard: {
      ...base.jobCard,
      border: '1px solid rgba(71, 85, 105, 0.45)',
      background: 'rgba(30, 41, 59, 0.4)',
      boxShadow: '0 1px 3px rgba(0,0,0,0.25)'
    },
    dateValue: { ...base.dateValue, color: '#f1f5f9' },
    timeValue: { ...base.timeValue, color: '#94a3b8' },
    jobType: { ...base.jobType, color: '#f1f5f9' },
    jobAddress: { ...base.jobAddress, color: '#94a3b8' },
    inquiryPreview: { ...base.inquiryPreview, color: '#cbd5e1' },
    jobCrew: { ...base.jobCrew, color: '#94a3b8' },
    reviewsList: {
      ...base.reviewsList,
      background: 'rgba(15, 23, 42, 0.35)'
    },
    reviewCard: {
      ...base.reviewCard,
      background: 'rgba(30, 41, 59, 0.55)'
    },
    reviewJobType: { ...base.reviewJobType, color: '#f1f5f9' },
    reviewDate: { ...base.reviewDate, color: '#94a3b8' },
    reviewRequestText: { ...base.reviewRequestText, color: '#94a3b8' },
    modalContent: {
      ...base.modalContent,
      background: 'rgba(30, 41, 59, 0.98)',
      border: '1px solid #475569'
    },
    modalHeader: {
      ...base.modalHeader,
      borderBottom: '1px solid #475569'
    },
    modalTitle: { ...base.modalTitle, color: '#f1f5f9' },
    closeButton: { ...base.closeButton, color: '#94a3b8' },
    photoCaption: { ...base.photoCaption, color: '#94a3b8' },
    noPhotos: { ...base.noPhotos, color: '#94a3b8' }
  };
}

const PortalDashboard = () => {
  console.log('PortalDashboard component starting');
  
  try {
    return <DashboardContent />;
  } catch (err) {
    console.error('Dashboard render error:', err);
    return (
      <div style={{padding: '40px', textAlign: 'center', background: 'white'}}>
        <h1>Error Loading Dashboard</h1>
        <p>{err.message}</p>
        <pre style={{textAlign: 'left', background: '#f5f5f5', padding: '20px', marginTop: '20px'}}>
          {err.stack}
        </pre>
        <button onClick={() => window.location.reload()} style={{padding: '10px 20px', marginTop: '20px'}}>
          Refresh Page
        </button>
      </div>
    );
  }
};

const DashboardContent = () => {
  const [invoices, setInvoices] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showPhotos, setShowPhotos] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [initialCheckDone, setInitialCheckDone] = useState(false);
  const [inquiries, setInquiries] = useState([]);
  const { customer, isAuthenticated, loading: authLoading, logout, fetchInvoices, fetchJobs, fetchServiceRequests, refreshSession } = usePortal();

  // Check session on mount (for cases where user just logged in)
  // Retries a few times to handle cookie propagation delays
  useEffect(() => {
    const checkWithRetry = async () => {
      const maxAttempts = 3;

      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        console.log(`[Dashboard] Session check attempt ${attempt}/${maxAttempts}`);
        await refreshSession();

        // Small delay between attempts to let cookie/state settle
        if (attempt < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      }

      setInitialCheckDone(true);
    };

    checkWithRetry();
  }, [refreshSession]);

  useEffect(() => {
    console.log('Dashboard effect running, isAuthenticated:', isAuthenticated, 'authLoading:', authLoading, 'initialCheckDone:', initialCheckDone);
    // Only proceed after initial auth check is complete
    if (!initialCheckDone) return;

    if (isAuthenticated) {
      loadData();
    }
  }, [isAuthenticated, initialCheckDone]);

  const [portalDark, setPortalDark] = useState(() => {
    try {
      return localStorage.getItem('portal_dark_mode') === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('portal_dark_mode', portalDark ? '1' : '0');
    } catch (_) {}
  }, [portalDark]);

  const st = useMemo(() => mergePortalStyles(styles, portalDark), [portalDark]);

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      const [invoicesData, jobsData, serviceRows] = await Promise.all([
        fetchInvoices(),
        fetchJobs(),
        fetchServiceRequests().catch(() => [])
      ]);

      setInvoices(invoicesData || []);
      setJobs(jobsData || []);
      setInquiries(
        (serviceRows || []).map((r) => ({
          id: r.id,
          issue: r.description,
          preferred_date: r.scheduled_date,
          preferred_time: r.scheduled_time,
          created_at: r.created_at,
          status: r.status,
          job_id: r.job_id
        }))
      );
      
      // Check for reviews from completed jobs (including jobs with linked invoices)
      const completedJobs = (jobsData || []).filter((job) => {
        if (!job?.id) return false;
        const jobHasInvoice = (invoicesData || []).some((invoice) => {
          const invoiceJobId = invoice.job_id || invoice.jobId || invoice.jobs?.id;
          return String(invoiceJobId) === String(job.id);
        });
        return job.status === 'completed' || jobHasInvoice;
      });
      const mockReviews = completedJobs.map(job => ({
        jobId: job.id,
        hasReview: Math.random() > 0.7, // Mock data - replace with actual review check
        rating: 5,
        date: job.completed_at
      }));
      setReviews(mockReviews);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
  };

  const handleJobClick = (job) => {
    setSelectedJob(job);
    setShowPhotos(true);
  };

  const hasInvoiceForJob = (jobId) => {
    if (!jobId) return false;
    return invoices.some((invoice) => {
      const invoiceJobId = invoice.job_id || invoice.jobId || invoice.jobs?.id;
      return String(invoiceJobId) === String(jobId);
    });
  };

  const getEffectiveJobStatus = (job) => {
    if (hasInvoiceForJob(job.id)) return 'completed';
    return job.status;
  };

  /** Badge: amber Inquiry requested → blue Inquiry when accepted → green ✓ Completed when won or linked job done */
  const getInquiryScheduleBadge = (inq) => {
    const s = String(inq.status || '').toLowerCase();
    const linkedJob =
      inq.job_id && jobs?.length ? jobs.find((j) => String(j.id) === String(inq.job_id)) : null;
    const jobEff = linkedJob ? getEffectiveJobStatus(linkedJob) : null;

    if (s === 'won' || jobEff === 'completed') {
      return {
        label: '✓ Completed',
        background: 'rgba(34, 197, 94, 0.15)',
        color: '#22c55e'
      };
    }
    if (s === 'lost') {
      return {
        label: 'Closed',
        background: 'rgba(107, 114, 128, 0.15)',
        color: '#6b7280'
      };
    }
    if (['new', 'quoted', 'inspection_scheduled'].includes(s)) {
      return {
        label: 'Inquiry',
        background: 'rgba(59, 130, 246, 0.15)',
        color: '#2563eb'
      };
    }
    return {
      label: 'Inquiry requested',
      background: 'rgba(245, 158, 11, 0.15)',
      color: '#d97706'
    };
  };

  // Calculate statistics
  const outstandingInvoices = invoices.filter(i => i.status !== 'paid');
  const paidInvoices = invoices.filter(i => i.status === 'paid');
  const completedJobs = jobs.filter(job => getEffectiveJobStatus(job) === 'completed');

  const outstandingAmount = outstandingInvoices.reduce((sum, i) => {
    const amount = i.total || i.amount || 0;
    return sum + parseFloat(amount);
  }, 0);

  const paidAmount = paidInvoices.reduce((sum, i) => {
    const amount = i.total || i.amount || 0;
    return sum + parseFloat(amount);
  }, 0);
  const showOutstandingCard = outstandingAmount > 1;

  const scheduleRows = mergeScheduleRows(jobs, inquiries);

  /** Portal inquiry: show Inspection after owner has confirmed (pipeline); unconfirmed stays Service request */
  const inquiryCardTitle = (status) => {
    const s = String(status || '').toLowerCase();
    if (['new', 'quoted', 'inspection_scheduled', 'won'].includes(s)) return 'Inspection';
    return 'Service request';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const t = timeString.length <= 5 ? `${timeString}:00` : timeString;
    return new Date(`2000-01-01T${t}`).toLocaleTimeString('en-AU', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return '#22c55e';
      case 'overdue':
        return '#ef4444';
      case 'pending':
      case 'sent':
        return '#f59e0b';
      default:
        return '#6b7280';
    }
  };

  const getJobStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return '#22c55e';
      case 'in_progress':
        return '#3b82f6';
      case 'scheduled':
        return '#f59e0b';
      default:
        return '#6b7280';
    }
  };

  console.log('DashboardContent rendering, authLoading:', authLoading, 'isAuthenticated:', isAuthenticated, 'initialCheckDone:', initialCheckDone);

  // Show loading while checking auth (including initial check)
  if (authLoading || !initialCheckDone) {
    console.log('Showing auth loading state');
    return (
      <div style={st.container}>
        <div aria-hidden="true" style={st.diagonalBackground}>
          <div aria-hidden="true" style={st.diagonalBar} />
        </div>
        <nav style={st.navbar}>
          <div style={st.navContent}>
            <div style={st.navLeft}>
              <div style={st.navLogo}>🔧</div>
              <h1 style={st.navTitle}>Customer Portal</h1>
            </div>
          </div>
        </nav>
        <main style={st.main}>
          <div style={st.loadingState}>
            <div style={st.spinner}></div>
            <p>Loading...</p>
          </div>
        </main>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div style={st.container}>
        <div aria-hidden="true" style={st.diagonalBackground}>
          <div aria-hidden="true" style={st.diagonalBar} />
        </div>
        <nav style={st.navbar}>
          <div style={st.navContent}>
            <div style={st.navLeft}>
              <div style={st.navLogo}>🔧</div>
              <h1 style={st.navTitle}>Customer Portal</h1>
            </div>
          </div>
        </nav>
        <main style={st.main}>
          <div style={st.errorContainer}>
            <div style={st.errorIcon}>🔒</div>
            <h2 style={st.errorTitle}>Please Log In</h2>
            <p style={st.errorMessage}>You need to log in to view your dashboard.</p>
            <Link to="/portal/login" style={st.loginButton}>
              Go to Login
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div style={st.container}>
      {/* Diagonal gradient background */}
      <div style={st.diagonalBackground}>
        <div style={st.diagonalBar}></div>
      </div>

      {/* Navigation */}
      <nav style={st.navbar}>
        <div style={st.navContent}>
          <div style={st.navLeft}>
            <div style={st.navLogo}>🔧</div>
            <h1 style={st.navTitle}>Customer Portal</h1>
          </div>
          <div style={st.navRight}>
            {customer && (
              <span style={st.customerName}>{customer.name}</span>
            )}
            <button onClick={handleLogout} style={st.logoutButton}>
              Logout
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main style={st.main}>
        <div style={st.content}>
          {/* Welcome Section */}
          <div style={st.welcomeRow}>
            <div style={st.welcomeTextBlock}>
              <h2 style={st.welcomeTitle}>
                Welcome back, {customer?.name || 'Customer'}
              </h2>
              <p style={st.welcomeSubtitle}>
                Viewing your jobs and invoices for {customer?.address || 'your address'}
              </p>
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 10,
                flexShrink: 0,
                alignSelf: 'flex-start',
                marginTop: '2px'
              }}
            >
              <Link to="/portal/service-request" style={{ ...st.serviceRequestButton, marginTop: 0 }}>
                Request service
              </Link>
              <PortalNotificationBell jobs={jobs} inquiries={inquiries} darkMode={portalDark} />
              <button
                type="button"
                onClick={() => setPortalDark((d) => !d)}
                aria-label={portalDark ? 'Switch to light mode' : 'Switch to dark mode'}
                style={{
                  position: 'relative',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 44,
                  height: 44,
                  borderRadius: 10,
                  border: portalDark
                    ? '1px solid rgba(148, 163, 184, 0.45)'
                    : '1px solid rgba(148, 163, 184, 0.5)',
                  background: portalDark ? 'rgba(30, 41, 59, 0.85)' : 'rgba(255,255,255,0.85)',
                  color: portalDark ? '#e2e8f0' : '#334155',
                  cursor: 'pointer',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                  marginTop: 2
                }}
              >
                {portalDark ? (
                  <svg width={22} height={22} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                    <path
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z"
                    />
                  </svg>
                ) : (
                  <svg width={22} height={22} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                    <path
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {error && (
            <div style={st.errorBox}>
              <span>⚠️</span> {error}
            </div>
          )}

          {/* Stats Cards */}
          <div style={st.statsGrid}>
            {showOutstandingCard && (
              <div style={{ ...st.statCard, borderTopColor: '#ef4444' }}>
                <p style={st.statLabel}>Outstanding</p>
                <p style={{ ...st.statValue, color: '#ef4444' }}>
                  {formatCurrency(outstandingAmount)}
                </p>
                <p style={st.statSubtext}>
                  {outstandingInvoices.length} unpaid
                </p>
              </div>
            )}

            <div style={{ ...st.statCard, ...st.statCardDark, borderTopColor: '#10b981' }}>
              <div style={{ ...st.cardTintOverlay, background: 'rgba(52, 211, 153, 0.38)' }}></div>
              <p style={{ ...st.statLabel, color: '#ffffff' }}>Paid</p>
              <p style={{ ...st.statValue, color: '#ffffff' }}>
                {formatCurrency(paidAmount)}
              </p>
              <p style={{ ...st.statSubtext, color: '#ffffff' }}>
                {paidInvoices.length} invoices
              </p>
            </div>

            <div style={{ ...st.statCard, ...st.statCardDark, borderTopColor: '#60a5fa' }}>
              <div style={{ ...st.cardTintOverlay, background: 'rgba(96, 165, 250, 0.38)' }}></div>
              <p style={{ ...st.statLabel, color: '#ffffff' }}>Jobs Completed</p>
              <p style={{ ...st.statValue, color: '#ffffff' }}>
                {completedJobs.length}
              </p>
              <p style={{ ...st.statSubtext, color: '#ffffff' }}>
                Total jobs
              </p>
            </div>
          </div>

          {/* Jobs Section */}
          <div style={st.section}>
            <div style={st.sectionHeader}>
              <h3 style={st.sectionTitle}>🔧 Your Jobs</h3>
              <Link to="/portal/jobs" style={st.viewAllLink}>
                View All →
              </Link>
            </div>

            {loading ? (
              <div style={st.loadingState}>
                <div style={st.spinner}></div>
                <p>Loading jobs...</p>
              </div>
            ) : scheduleRows.length === 0 ? (
              <div style={st.emptyState}>
                <div style={st.emptyIcon}>🔧</div>
                <p>No jobs yet</p>
              </div>
            ) : (
              <div style={st.jobsList}>
                {scheduleRows.map((row) => {
                  if (row.kind === 'inquiry') {
                    const inq = row.inquiry;
                    const preview = inq.issue.length > 100 ? `${inq.issue.slice(0, 97)}…` : inq.issue;
                    const inqBadge = getInquiryScheduleBadge(inq);
                    return (
                      <div key={inq.id} style={st.jobCard}>
                        <div style={st.jobHeader}>
                          <div style={st.jobDate}>
                            <div style={st.dateIcon}>📅</div>
                            <div>
                              <p style={st.dateValue}>{formatDate(inq.preferred_date || inq.created_at)}</p>
                              {inq.preferred_time && (
                                <p style={st.timeValue}>{formatTime(inq.preferred_time)}</p>
                              )}
                            </div>
                          </div>
                          <span
                            style={{
                              ...st.statusBadge,
                              background: inqBadge.background,
                              color: inqBadge.color
                            }}
                          >
                            {inqBadge.label}
                          </span>
                        </div>
                        <div style={st.jobBody}>
                          <h3 style={st.jobType}>{inquiryCardTitle(inq.status)}</h3>
                          <p style={st.inquiryPreview}>{preview}</p>
                          {customer?.address && (
                            <p style={st.jobAddress}>📍 {customer.address}</p>
                          )}
                        </div>
                      </div>
                    );
                  }

                  const job = row.job;
                  const effectiveStatus = getEffectiveJobStatus(job);
                  return (
                    <div
                      key={job.id}
                      style={st.jobCard}
                      onClick={() => handleJobClick(job)}
                    >
                      <div style={st.jobHeader}>
                        <div style={st.jobDate}>
                          <div style={st.dateIcon}>📅</div>
                          <div>
                            <p style={st.dateValue}>{formatDate(job.scheduled_date)}</p>
                            {job.scheduled_time && (
                              <p style={st.timeValue}>{formatTime(job.scheduled_time)}</p>
                            )}
                          </div>
                        </div>
                        <span style={{
                          ...st.statusBadge,
                          background: `${getJobStatusColor(effectiveStatus)}20`,
                          color: getJobStatusColor(effectiveStatus)
                        }}>
                          {effectiveStatus === 'completed' ? '✓ Completed' : effectiveStatus}
                        </span>
                      </div>

                      <div style={st.jobBody}>
                        <h3 style={st.jobType}>{job.job_type}</h3>

                        <p style={st.jobAddress}>📍 {job.address}</p>

                        {job.crew_assigned && job.crew_assigned.length > 0 && (
                          <p style={st.jobCrew}>
                            🔧 Technician: {job.crew_assigned.join(', ')}
                          </p>
                        )}

                        {job.job_photos && job.job_photos.length > 0 && (
                          <p style={st.jobPhotos}>
                            📸 {job.job_photos.length} photos available
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Invoices Section */}
          <div style={st.section}>
            <div style={st.sectionHeader}>
              <h3 style={st.sectionTitle}>📄 Your Invoices</h3>
              <Link to="/portal/invoices" style={st.viewAllLink}>
                View All →
              </Link>
            </div>

            {loading ? (
              <div style={st.loadingState}>
                <div style={st.spinner}></div>
                <p>Loading invoices...</p>
              </div>
            ) : invoices.length === 0 ? (
              <div style={st.emptyState}>
                <div style={st.emptyIcon}>📄</div>
                <p>No invoices yet</p>
              </div>
            ) : (
              <div style={st.table}>
                <div style={st.tableHeader}>
                  <span style={st.invoiceCol}>Invoice</span>
                  <span style={st.dateCol}>Date</span>
                  <span style={st.statusCol}>Status</span>
                  <span style={st.amountCol}>Amount</span>
                  <span style={st.actionCol}></span>
                </div>
                {invoices.map((invoice) => (
                  <div key={invoice.id} style={st.tableRow}>
                    <div style={st.invoiceCol}>
                      <p style={st.invoiceNumber}>{invoice.invoice_number}</p>
                    </div>
                    <span style={st.dateCol}>
                      {formatDate(invoice.date)}
                    </span>
                    <span style={st.statusCol}>
                      <span style={{
                        ...st.statusBadge,
                        background: `${getStatusColor(invoice.status)}20`,
                        color: getStatusColor(invoice.status)
                      }}>
                        {invoice.status === 'paid' ? '✓ Paid' :
                         invoice.status === 'overdue' ? '⚠ Overdue' :
                         invoice.status === 'pending' ? '⏳ Pending' :
                         invoice.status}
                      </span>
                    </span>
                    <span style={st.amountCol}>
                      {formatCurrency(invoice.total || invoice.amount)}
                    </span>
                    <div style={st.actionCol}>
                      <Link
                        to={`/portal/invoices/${invoice.id}`}
                        style={st.viewLink}
                      >
                        View
                      </Link>
                      {invoice.status !== 'paid' && (
                        <Link
                          to={`/portal/invoices/${invoice.id}?pay=true`}
                          style={st.payLink}
                        >
                          Pay
                        </Link>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Google Reviews Section */}
          <div style={st.section}>
            <div style={st.sectionHeader}>
              <h3 style={st.sectionTitle}>⭐ Reviews</h3>
            </div>
            
            {completedJobs.length === 0 ? (
              <div style={st.emptyState}>
                <p style={st.emptyText}>Complete a job to leave a review</p>
              </div>
            ) : (
              <div style={st.reviewsList}>
                {completedJobs.map((job) => {
                  const review = reviews.find(r => r.jobId === job.id);
                  const hasReview = review?.hasReview;
                  
                  return (
                    <div key={job.id} style={st.reviewCard}>
                      <div style={st.reviewJobInfo}>
                        <p style={st.reviewJobType}>{job.job_type}</p>
                        <p style={st.reviewDate}>{formatDate(job.scheduled_date)}</p>
                      </div>
                      
                      {hasReview ? (
                        <div style={st.reviewGiven}>
                          <div style={st.stars}>
                            {'⭐'.repeat(review?.rating || 5)}
                          </div>
                          <p style={st.reviewText}>Thank you for your review!</p>
                        </div>
                      ) : (
                        <div style={st.reviewRequest}>
                          <p style={st.reviewRequestText}>
                            How was your service?
                          </p>
                          <a 
                            href={process.env.REACT_APP_GOOGLE_REVIEW_LINK || '#'} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={st.reviewButton}
                          >
                            Leave a Google Review
                          </a>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Photo Modal */}
      {showPhotos && selectedJob && (
        <div style={st.modal} onClick={() => setShowPhotos(false)}>
          <div style={st.modalContent} onClick={e => e.stopPropagation()}>
            <div style={st.modalHeader}>
              <h3 style={st.modalTitle}>{selectedJob.job_type} - Photos</h3>
              <button 
                onClick={() => setShowPhotos(false)} 
                style={st.closeButton}
              >
                ✕
              </button>
            </div>
            
            <div style={st.photosGrid}>
              {selectedJob.job_photos && selectedJob.job_photos.length > 0 ? (
                selectedJob.job_photos.map((photo, idx) => (
                  <div key={idx} style={st.photoContainer}>
                    <img 
                      src={photo.url} 
                      alt={`Job photo ${idx + 1}`}
                      style={st.photo}
                    />
                    {photo.caption && (
                      <p style={st.photoCaption}>{photo.caption}</p>
                    )}
                  </div>
                ))
              ) : (
                <div style={st.noPhotos}>
                  <p>No photos available for this job</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    background: PORTAL_LOGIN_GRADIENT,
    display: 'flex',
    flexDirection: 'column',
    position: 'relative'
  },
  diagonalBackground: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 0,
    pointerEvents: 'none',
    overflow: 'hidden'
  },
  diagonalBar: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: '340%',
    height: '260px',
    transform: 'translate(-50%, -50%) rotate(-12deg)',
    transformOrigin: 'center',
    ...PORTAL_LOGIN_DIAGONAL_BAR,
    filter: 'blur(80px)',
    overflow: 'hidden'
  },
  navbar: {
    background: 'rgba(255, 255, 255, 0.8)',  // 80% transparent white
    borderBottom: '1px solid rgba(229, 231, 235, 0.8)',
    position: 'sticky',
    top: 0,
    zIndex: 2,  // Higher than background
    width: '100%',
    backdropFilter: 'blur(10px)'  // Add blur effect
  },
  navContent: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '16px 24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%'
  },
  navLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  navLogo: {
    fontSize: '24px'
  },
  navTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#111827',
    margin: 0
  },
  navRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
  },
  customerName: {
    color: '#6b7280',
    fontSize: '14px'
  },
  logoutButton: {
    background: 'transparent',
    border: '1px solid #d1d5db',
    color: '#6b7280',
    padding: '8px 16px',
    borderRadius: '6px',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  /** Mirrors PortalLogin `styles.inner` (same shell over gradient + diagonal; nav consumes top space) */
  main: {
    position: 'relative',
    zIndex: 1,
    minHeight: 'calc(100vh - 73px)',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    padding: '20px',
    boxSizing: 'border-box',
    flex: '1 1 0%',
    background: 'transparent'
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    width: '100%',
    maxWidth: '1200px'
  },
  welcomeSection: {
    marginBottom: '8px'
  },
  welcomeRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '16px',
    marginBottom: '8px',
    flexWrap: 'wrap'
  },
  welcomeTextBlock: {
    flex: '1 1 280px',
    minWidth: 0
  },
  serviceRequestButton: {
    flexShrink: 0,
    alignSelf: 'flex-start',
    marginTop: '2px',
    background: '#f97316',
    color: '#ffffff',
    padding: '10px 18px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 600,
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    boxShadow: '0 1px 2px rgba(0,0,0,0.06)'
  },
  welcomeTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#111827',
    margin: '0 0 4px 0'
  },
  welcomeSubtitle: {
    fontSize: '15px',
    color: '#6b7280',
    margin: '0 0 4px 0'
  },
  addressNote: {
    fontSize: '13px',
    color: '#f97316',
    margin: '8px 0 0 0',
    fontWeight: '500'
  },
  errorBox: {
    background: 'rgba(254, 242, 242, 0.8)',  // 80% transparent
    border: '1px solid rgba(254, 202, 202, 0.8)',
    borderRadius: '8px',
    padding: '12px 16px',
    color: '#dc2626',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backdropFilter: 'blur(10px)'
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '12px',
    marginTop: '-6px'
  },
  statCard: {
    background: 'rgba(255, 255, 255, 0.60)',
    borderRadius: '12px',
    padding: '20px',
    borderTop: '4px solid',
    boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
    backdropFilter: 'blur(14px)',
    WebkitBackdropFilter: 'blur(14px)',
    border: '1px solid rgba(255, 255, 255, 0.65)',
    position: 'relative',
    overflow: 'hidden'
  },
  statCardDark: {
    background: 'linear-gradient(145deg, rgba(36,52,71,0.92) 0%, rgba(30,41,59,0.92) 50%, rgba(26,34,50,0.92) 100%)',
    border: '1px solid #334155'
  },
  cardTintOverlay: {
    position: 'absolute',
    inset: 0,
    borderRadius: '12px',
    pointerEvents: 'none'
  },
  statLabel: {
    fontSize: '14px',
    color: '#6b7280',
    margin: '0 0 8px 0',
    position: 'relative',
    zIndex: 1
  },
  statLabelDark: {
    color: '#e2e8f0'
  },
  statValue: {
    fontSize: '28px',
    fontWeight: '700',
    margin: '0 0 4px 0',
    position: 'relative',
    zIndex: 1
  },
  statSubtext: {
    fontSize: '13px',
    color: '#9ca3af',
    margin: 0,
    position: 'relative',
    zIndex: 1
  },
  statSubtextDark: {
    color: '#cbd5e1'
  },
  section: {
    background: 'rgba(255, 255, 255, 0.3)',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    overflow: 'hidden',
    backdropFilter: 'blur(10px)'  // Add blur effect
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid rgba(229, 231, 235, 0.3)',
    background: 'rgba(255, 255, 255, 0.3)'
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#111827',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  viewAllLink: {
    color: '#f97316',
    textDecoration: 'none',
    fontSize: '14px',
    fontWeight: '500'
  },
  loadingState: {
    padding: '40px',
    textAlign: 'center',
    color: '#6b7280',
    background: 'rgba(255, 255, 255, 0.3)'
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: '3px solid #e5e7eb',
    borderTopColor: '#f97316',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
    margin: '0 auto 16px'
  },
  emptyState: {
    padding: '40px',
    textAlign: 'center',
    color: '#9ca3af',
    background: 'rgba(255, 255, 255, 0.3)'
  },
  emptyIcon: {
    fontSize: '40px',
    marginBottom: '12px'
  },
  emptyText: {
    fontSize: '14px',
    color: '#6b7280'
  },
  table: {
    display: 'flex',
    flexDirection: 'column'
  },
  tableHeader: {
    display: 'grid',
    gridTemplateColumns: 'minmax(110px, 2fr) minmax(80px, 1fr) minmax(90px, 1fr) minmax(80px, 1fr) auto',
    gap: '10px',
    padding: '16px 24px',
    background: 'rgba(249, 250, 251, 0.3)',
    borderBottom: '1px solid rgba(229, 231, 235, 0.3)',
    fontSize: '12px',
    fontWeight: '600',
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  tableRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(110px, 2fr) minmax(80px, 1fr) minmax(90px, 1fr) minmax(80px, 1fr) auto',
    gap: '10px',
    padding: '16px 24px',
    borderBottom: '1px solid rgba(243, 244, 246, 0.3)',
    alignItems: 'center',
    transition: 'background 0.2s',
    background: 'rgba(255, 255, 255, 0.3)'
  },
  invoiceCol: {
    display: 'flex',
    flexDirection: 'column'
  },
  invoiceNumber: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#111827',
    margin: 0
  },
  dateCol: {
    fontSize: '14px',
    color: '#6b7280'
  },
  statusCol: {},
  statusBadge: {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: '9999px',
    fontSize: '12px',
    fontWeight: '500'
  },
  amountCol: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#111827'
  },
  actionCol: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'flex-end'
  },
  viewLink: {
    color: '#f97316',
    textDecoration: 'none',
    fontSize: '14px',
    fontWeight: '500',
    padding: '4px 8px'
  },
  payLink: {
    background: '#f97316',
    color: 'white',
    textDecoration: 'none',
    fontSize: '13px',
    fontWeight: '500',
    padding: '6px 12px',
    borderRadius: '6px'
  },
  jobsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    padding: '12px 16px 16px'
  },
  jobCard: {
    padding: '20px 24px',
    borderRadius: '10px',
    border: '1px solid rgba(203, 213, 225, 0.25)',
    cursor: 'pointer',
    transition: 'background 0.2s, box-shadow 0.2s',
    background: 'rgba(229, 231, 235, 0.25)',
    boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)'
  },
  jobHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '12px'
  },
  jobDate: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  dateIcon: {
    fontSize: '20px'
  },
  dateValue: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#111827',
    margin: 0
  },
  timeValue: {
    fontSize: '13px',
    color: '#6b7280',
    margin: '2px 0 0 0'
  },
  jobBody: {},
  jobType: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#111827',
    margin: '0 0 8px 0'
  },
  jobAddress: {
    fontSize: '14px',
    color: '#6b7280',
    margin: '0 0 8px 0'
  },
  inquiryPreview: {
    fontSize: '14px',
    color: '#4b5563',
    margin: '0 0 8px 0',
    lineHeight: 1.45,
    whiteSpace: 'pre-wrap'
  },
  jobCrew: {
    fontSize: '13px',
    color: '#6b7280',
    margin: '0 0 4px 0'
  },
  jobPhotos: {
    fontSize: '13px',
    color: '#f97316',
    margin: '8px 0 0 0',
    fontWeight: '500'
  },
  reviewsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    padding: '20px 24px',
    background: 'rgba(255, 255, 255, 0.3)'
  },
  reviewCard: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px',
    background: 'rgba(249, 250, 251, 0.3)',
    borderRadius: '8px'
  },
  reviewJobInfo: {},
  reviewJobType: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#111827',
    margin: '0 0 4px 0'
  },
  reviewDate: {
    fontSize: '13px',
    color: '#6b7280',
    margin: 0
  },
  reviewGiven: {
    textAlign: 'right'
  },
  stars: {
    fontSize: '16px',
    marginBottom: '4px'
  },
  reviewText: {
    fontSize: '13px',
    color: '#22c55e',
    margin: 0
  },
  reviewRequest: {
    textAlign: 'right'
  },
  reviewRequestText: {
    fontSize: '13px',
    color: '#6b7280',
    margin: '0 0 8px 0'
  },
  reviewButton: {
    display: 'inline-block',
    background: '#4285f4',
    color: 'white',
    padding: '8px 16px',
    borderRadius: '6px',
    textDecoration: 'none',
    fontSize: '13px',
    fontWeight: '500'
  },
  modal: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '24px'
  },
  modalContent: {
    background: 'rgba(255, 255, 255, 0.95)',  // Slightly less transparent for readability
    borderRadius: '12px',
    maxWidth: '800px',
    width: '100%',
    maxHeight: '90vh',
    overflow: 'auto',
    backdropFilter: 'blur(10px)'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid #e5e7eb'
  },
  modalTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#111827',
    margin: 0
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    color: '#6b7280'
  },
  photosGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
    gap: '16px',
    padding: '24px'
  },
  photoContainer: {
    borderRadius: '8px',
    overflow: 'hidden'
  },
  photo: {
    width: '100%',
    height: '200px',
    objectFit: 'cover'
  },
  photoCaption: {
    fontSize: '13px',
    color: '#6b7280',
    margin: '8px 0 0 0',
    textAlign: 'center'
  },
  noPhotos: {
    padding: '40px',
    textAlign: 'center',
    color: '#6b7280'
  },
  errorContainer: {
    textAlign: 'center',
    padding: '60px 20px',
    background: 'rgba(255, 255, 255, 0.8)',  // 80% transparent
    borderRadius: '12px',
    backdropFilter: 'blur(10px)'
  },
  errorIcon: {
    fontSize: '48px',
    marginBottom: '16px'
  },
  errorTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#111827',
    margin: '0 0 8px 0'
  },
  errorMessage: {
    color: '#6b7280',
    margin: '0 0 24px 0'
  },
  loginButton: {
    display: 'inline-block',
    background: '#f97316',
    color: 'white',
    padding: '12px 24px',
    borderRadius: '8px',
    textDecoration: 'none',
    fontWeight: '600'
  }
};

export default PortalDashboard;
